import { now } from '@calendly/datetime'
import { CheckboxField, SubmitFormButton, TextField, useFormContext, useFormField } from '@calendly/form'
import { FieldError } from '@calendly/form/parts'
import { I18n } from '@calendly/i18n'
import { DatePicker } from '@calendly/ui/components/date-picker'
import { IconCalendar } from '@calendly/ui/components/glyphs/IconCalendar'
import { IconChevronDown } from '@calendly/ui/components/glyphs/IconChevronDown'
import { IconClock } from '@calendly/ui/components/glyphs/IconClock'
import { LabelText, TitleText } from '@calendly/ui/components/typography'
import { UiIcon } from '@calendly/ui/components/ui-icon'
import { ViewSwitcher } from '@calendly/ui/components/view-switcher'
import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
import { useMemo, useState, useEffect, useRef } from 'react'

import { useEventType } from 'features/event_type_provider'

import {
  AmountContainer,
  AmountInput,
  AmountInputContainer,
  DatePickerOverlay,
  Description,
  DiscountTypeSwitcherWrapper,
  ExpirationDateButton,
  ExpirationDateContainer,
  FieldGroup,
  FixedAmountContainer,
  FixedAmountInput,
  FixedAmountPrefix,
  FixedAmountWrapper,
  Footer,
  FormContainer,
  InlineCouponBody,
  InlineCouponOptionRow,
  InlineExpirationFields,
  OptionsSection,
  PanelBody,
  PercentSymbol,
  SelectedDateText,
  StyledBanner,
  StyledCodeField,
  StyledForm,
  TimeClockCircleIconContainer,
  TimeSelectContainer,
  TimeSelectField,
  UsageLimitContainer,
  UsageLimitInput,
  UsageLimitText
} from './create_coupon_modal_styles'
import { buildValidationSchema, parseAndFormatDate } from './create_coupon_modal_utils'
import { SidePanel } from '../../../components/side_panel'

const CURRENCY_SYMBOLS = {
  AUD: '$',
  CAD: '$',
  EUR: '€',
  GBP: '£',
  USD: '$',
}

const getDiscountTypeOptions = () => [
  {
    value: 'percentage',
    text: I18n.t('publisher.event_types.payment_method.percentage_off'),
  },
  {
    value: 'fixed',
    text: I18n.t('publisher.event_types.payment_method.fixed_amount_off'),
  },
]

const DiscountTypeSwitcher = ({ value, onChange, disabled = false }) => (
  <DiscountTypeSwitcherWrapper disabled={disabled} data-testid="discount-type-switcher">
    <ViewSwitcher
      name="coupon-discount-type"
      value={value}
      options={getDiscountTypeOptions()}
      onChange={(event) => {
        if (!disabled) {
          onChange(event.target.value)
        }
      }}
    />
  </DiscountTypeSwitcherWrapper>
)

export const getCouponFormInitialValues = () => ({
  name: '',
  code: '',
  type: 'percentage',
  amount: '',
  firstTimeOnly: false,
  limitUsage: false,
  usageLimit: '',
  addExpiration: false,
  expirationDate: now().plus({ days: 30 }).toFormat('yyyy-MM-dd'),
  expirationTime: '23:45',
})

const getCouponFormValuesFromCoupon = (coupon) => {
  const endDate = coupon.endAt ? parseAndFormatDate(coupon.endAt) : null

  return {
    name: coupon.description || '',
    code: coupon.code || '',
    type: coupon.bookingCodeType === 'PERCENT' ? 'percentage' : 'fixed',
    amount: coupon.bookingCodeType === 'PERCENT'
      ? coupon.percentOff
      : (coupon.fixedAmountCents / 100),
    firstTimeOnly: coupon.maxPerInvitee === 1,
    limitUsage: coupon.maxRedemptions != null,
    usageLimit: coupon.maxRedemptions || '',
    addExpiration: !!coupon.endAt,
    expirationDate: endDate
      ? endDate.toFormat('yyyy-MM-dd')
      : now().plus({ days: 30 }).toFormat('yyyy-MM-dd'),
    expirationTime: endDate
      ? endDate.toFormat('HH:mm')
      : '23:45',
  }
}

// Form Fields Component
export const CouponFormFields = ({
  currency,
  isEditMode,
  couponToEdit,
  apiError,
  variant = 'panel',
}) => {
  const { values, setFieldValue, setFieldError, setFieldTouched, resetForm } = useFormContext()
  const eventType = useEventType()
  const { available_currencies: availableCurrencies } = eventType || { available_currencies: [] }
  const [showDatePicker, setShowDatePicker] = useState(false)
  const isMobile = useDeviceType('isPhone')
  const inputRef = useRef(null)
  const isInline = variant === 'inline'

  // Check if coupon has started (startAt is not null)
  const isCouponStarted = Boolean(isEditMode && couponToEdit?.startAt)

  // Generate time options in 15-minute intervals from 0:00 (midnight) to 23:45 (11:45 PM)
  const timeOptions = useMemo(() => {
    const options = []
    for (let hour = 0; hour <= 23; hour += 1) {
      for (let minute = 0; minute < 60; minute += 15) {
        const time = now().set({ hour, minute, second: 0, millisecond: 0 })
        const value = time.toFormat('HH:mm')
        const label = time.toFormat('h:mma').toLowerCase()
        options.push({ value, name: label })
      }
    }
    return options
  }, [])

  const selectedCurrency = useMemo(
    () => availableCurrencies.find((curr) => curr.code === currency),
    [currency, availableCurrencies]
  )

  const handleTypeChange = (type) => {
    if (isCouponStarted) return
    setFieldValue('type', type)
    setFieldValue('amount', '')
    setFieldError('amount', undefined)
    setFieldTouched('amount', false)
  }

  // Reset values + validation state when editing a coupon or switching to create.
  // setFieldValue alone leaves prior errors/touched in place (validateOnChange is off).
  // Guard on coupon identity so parent re-renders (e.g. isLoading) do not recreate resetForm
  // and wipe in-progress input.
  const couponSyncKey = isEditMode
    ? (couponToEdit?.uuid ?? couponToEdit?.code)
    : 'create'
  const syncedCouponKeyRef = useRef(null)

  useEffect(() => {
    if (syncedCouponKeyRef.current === couponSyncKey) return
    syncedCouponKeyRef.current = couponSyncKey

    if (isEditMode && couponToEdit) {
      resetForm({ values: getCouponFormValuesFromCoupon(couponToEdit) })
    } else if (!isEditMode) {
      resetForm({ values: getCouponFormInitialValues() })
    }
  }, [isEditMode, couponSyncKey, couponToEdit, resetForm])

  // Handle API errors
  useEffect(() => {
    if (apiError && apiError.field) {
      setFieldError(apiError.field, apiError.message)
    }
  }, [apiError, setFieldError])

  const [, { error: expirationDateError }] = useFormField({ name: 'expirationDate' })
  const [amountFieldProps, { error: amountError }] = useFormField({ name: 'amount' })

  const currencySymbol = selectedCurrency?.symbol
    || CURRENCY_SYMBOLS[currency]
    || '$'

  const amountField = values.type === 'fixed' ? (
    <FixedAmountContainer>
      <FixedAmountWrapper data-invalid={amountError ? true : undefined}>
        <FixedAmountPrefix aria-hidden="true">{currencySymbol}</FixedAmountPrefix>
        <FixedAmountInput
          {...amountFieldProps}
          type="number"
          min={0.01}
          step={0.01}
          required
          disabled={isCouponStarted}
          inputMode="decimal"
          placeholder="0.00"
          size={isInline ? 'medium' : undefined}
          aria-label={I18n.t('publisher.event_type_editor_crud.payments.amount_label')}
          aria-invalid={Boolean(amountError)}
        />
      </FixedAmountWrapper>
      {amountError ? <FieldError text={amountError} /> : null}
    </FixedAmountContainer>
  ) : (
    <AmountContainer isInline={isInline}>
      <AmountInputContainer ref={inputRef}>
        <AmountInput
          name="amount"
          type="number"
          min={5}
          step={1}
          required
          disabled={isCouponStarted}
          inputMode="numeric"
          size={isInline ? 'medium' : undefined}
        />
      </AmountInputContainer>
      <PercentSymbol>%</PercentSymbol>
    </AmountContainer>
  )

  const usageLimitFields = values.limitUsage && (
    <UsageLimitContainer isInline={isInline}>
      <UsageLimitInput
        name="usageLimit"
        type="number"
        min="1"
        max="1000000"
        step="1"
        size={isInline ? 'medium' : undefined}
        isInline={isInline}
      />
      <UsageLimitText>{I18n.t('publisher.event_types.payment_method.times')}</UsageLimitText>
    </UsageLimitContainer>
  )

  const expirationDateField = (
    <ExpirationDateContainer isInline={isInline}>
      <ExpirationDateButton
        decoration="secondary-outline"
        size="small"
        isInline={isInline}
        hasError={!!expirationDateError}
        onClick={() => setShowDatePicker(!showDatePicker)}
        trailingIcon={isInline ? IconChevronDown : undefined}
      >
        <UiIcon icon={IconCalendar} size="small" title="" spacingRight={8} />
        {values.expirationDate ? (
          <SelectedDateText>
            {parseAndFormatDate(values.expirationDate)?.toFormat('MMMM d, yyyy') || values.expirationDate}
          </SelectedDateText>
        ) : (
          <SelectedDateText>{I18n.t('publisher.event_types.payment_method.select_expiration_date')}</SelectedDateText>
        )}
      </ExpirationDateButton>
      {expirationDateError ? <FieldError text={expirationDateError} /> : null}
      {showDatePicker && (
        <DatePickerOverlay isMobile={isMobile}>
          <DatePicker
            date={values.expirationDate}
            onChange={(date) => {
              setFieldValue('expirationDate', date)
              setShowDatePicker(false)
            }}
            minDate={now().toFormat('yyyy-MM-dd')}
            pastSelection={false}
          />
        </DatePickerOverlay>
      )}
    </ExpirationDateContainer>
  )

  const expirationTimeField = (
    <TimeSelectContainer isInline={isInline}>
      <TimeClockCircleIconContainer>
        <UiIcon icon={IconClock} size="small" title="" />
      </TimeClockCircleIconContainer>
      <TimeSelectField
        name="expirationTime"
        options={timeOptions}
        placeholder={I18n.t('publisher.event_types.payment_method.select_time')}
        size={isInline ? 'medium' : 'standard'}
        isInline={isInline}
      />
    </TimeSelectContainer>
  )

  const expirationFields = values.addExpiration && (
    isInline ? (
      <InlineExpirationFields>
        {expirationDateField}
        {expirationTimeField}
      </InlineExpirationFields>
    ) : (
      <>
        {expirationDateField}
        {expirationTimeField}
      </>
    )
  )

  if (isInline) {
    return (
      <>
        <InlineCouponBody>
          {isCouponStarted && (
            <StyledBanner type="system">
              {I18n.t('publisher.event_types.payment_method.coupon_redeemed_disabled_message')}
            </StyledBanner>
          )}

          <StyledCodeField
            autoFocus
            name="code"
            label={I18n.t('publisher.event_type_editor_crud.payments.coupon_code_label')}
            placeholder={I18n.t('publisher.event_type_editor_crud.payments.coupon_code_placeholder')}
            size="medium"
            required
            disabled={isCouponStarted}
          />

          <TextField
            name="name"
            label={I18n.t('publisher.event_types.payment_method.coupon_name')}
            placeholder={I18n.t('publisher.event_type_editor_crud.payments.coupon_name_placeholder')}
            trailingHelperText={I18n.t('publisher.event_type_editor_crud.payments.coupon_name_helper')}
            size="medium"
            required
            disabled={isCouponStarted}
          />

          <DiscountTypeSwitcher
            value={values.type}
            onChange={handleTypeChange}
            disabled={isCouponStarted}
          />

          {amountField}
        </InlineCouponBody>

        <InlineCouponOptionRow>
          <CheckboxField
            name="firstTimeOnly"
            label={I18n.t('publisher.event_types.payment_method.first_time_only')}
          />
        </InlineCouponOptionRow>

        <InlineCouponOptionRow>
          <CheckboxField
            name="limitUsage"
            label={I18n.t('publisher.event_type_editor_crud.payments.limit_usage')}
          />
          {usageLimitFields}
        </InlineCouponOptionRow>

        <InlineCouponOptionRow>
          <CheckboxField
            name="addExpiration"
            label={I18n.t('publisher.event_types.payment_method.add_expiration')}
          />
          {expirationFields}
        </InlineCouponOptionRow>
      </>
    )
  }

  return (
    <>
      {isCouponStarted && (
        <StyledBanner type="system">
          {I18n.t('publisher.event_types.payment_method.coupon_redeemed_disabled_message')}
        </StyledBanner>
      )}

      <FieldGroup>
        <StyledCodeField
          autoFocus
          name="code"
          label={I18n.t('publisher.event_types.payment_method.code')}
          size="standard"
          required
          disabled={isCouponStarted}
        />
      </FieldGroup>

      <FieldGroup>
        <TextField
          name="name"
          label={I18n.t('publisher.event_types.payment_method.coupon_name')}
          size="standard"
          required
          disabled={isCouponStarted}
        />
        <Description>{I18n.t('publisher.event_types.payment_method.coupon_name_description')}</Description>
      </FieldGroup>

      <FieldGroup>
        <LabelText size="large">{I18n.t('publisher.event_types.payment_method.type')}</LabelText>
        <DiscountTypeSwitcher
          value={values.type}
          onChange={handleTypeChange}
          disabled={isCouponStarted}
        />
        {amountField}
      </FieldGroup>

      <OptionsSection>
        <LabelText size="large">{I18n.t('publisher.event_types.payment_method.options')}</LabelText>
        <CheckboxField
          name="firstTimeOnly"
          label={I18n.t('publisher.event_types.payment_method.first_time_only')}
        />
        <CheckboxField
          name="limitUsage"
          label={I18n.t('publisher.event_types.payment_method.limit_usage')}
        />
        {usageLimitFields}
        <CheckboxField
          name="addExpiration"
          label={I18n.t('publisher.event_types.payment_method.add_expiration')}
        />
        {expirationFields}
      </OptionsSection>
    </>
  )
}

export const CouponModal = ({ isOpen, onClose, onSubmit, isLoading, isEditMode = false, couponToEdit = null, targetRef, apiError }) => {
  const { values: { currency } } = useFormContext()
  const isMobile = useDeviceType('isPhone')

  const validationSchema = useMemo(() => buildValidationSchema(), [])

  const handleSubmit = async (formValues) => {
    if (onSubmit) {
      await onSubmit(formValues, currency, isEditMode, couponToEdit)
    }
  }

  if (!isOpen) return null

  return (
    <SidePanel
      targetRef={targetRef}
      onClose={onClose}
      header={(
        <TitleText level={2} as="h1" size="medium">
          {isEditMode
            ? I18n.t('publisher.event_types.payment_method.edit_coupon_code')
            : I18n.t('publisher.event_types.payment_method.create_coupon_code')}
        </TitleText>
      )}
    >
      <PanelBody>
        <StyledForm
          initialValues={getCouponFormInitialValues()}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnChange={false}
          isMobile={isMobile}
        >
          <FormContainer>
            <CouponFormFields
              currency={currency}
              isEditMode={isEditMode}
              couponToEdit={couponToEdit}
              apiError={apiError}
            />
          </FormContainer>

          <Footer isMobile={isMobile}>
            <SubmitFormButton loading={isLoading}>
              {isEditMode ? I18n.t('publisher.event_types.payment_method.update') : I18n.t('publisher.event_types.payment_method.create')}
            </SubmitFormButton>
          </Footer>
        </StyledForm>
      </PanelBody>
    </SidePanel>
  )
}
